'use client';

import { useState } from 'react';
import { type NodeProps } from '@xyflow/react';
import { NodeShell } from '../BaseNode';
import { NODE_SPECS } from '@/lib/pipeline/registry';
import { type ParamField } from '@/lib/pipeline/types';
import { usePipelineStore } from '@/lib/pipeline/store';
import { MirroredInput } from '../MirroredInput';
import { PlacementPad } from '../PlacementPad';
import { useLivePreview } from '../useLivePreview';

// Param-heavy nodes collapse their controls behind a disclosure to keep the canvas tidy.
const COLLAPSE_THRESHOLD = 4;

// Nodes whose position params can be set by dragging on a pad over the image.
const POSITIONAL: Record<string, { mode: 'point' | 'rect'; bgHandle: string }> = {
  text: { mode: 'point', bgHandle: 'image' },
  composite: { mode: 'point', bgHandle: 'background' },
  shapes: { mode: 'rect', bgHandle: 'image' },
};

const num = (v: unknown, def: number) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : def;
};
const round2 = (n: number) => Math.round(n * 100) / 100;

/** Generic node for IMAGE→IMAGE operators: param controls + drag pad + per-param "expose". */
export default function OperatorNode({ id, type, data }: NodeProps) {
  const nodeType = type as string;
  const spec = NODE_SPECS[nodeType];
  const update = usePipelineStore((s) => s.updateNodeData);
  const rt = usePipelineStore((s) => s.runtime[id]);
  const out = rt?.output as
    | { image?: string; mask?: string; _ms?: number; _faces?: number; _info?: string }
    | undefined;
  const d = data as Record<string, unknown>;
  const params = spec?.params ?? [];

  const [open, setOpen] = useState(params.length <= COLLAPSE_THRESHOLD);
  // Live preview (node-local, not fed downstream) + this node's resolved upstream inputs.
  const { preview: livePreview, inputs } = useLivePreview(id, nodeType, d);

  const pos = POSITIONAL[nodeType];
  const padImage = pos
    ? livePreview ?? out?.image ?? (inputs[pos.bgHandle] as string | undefined)
    : undefined;
  const padAspect = num(d.width, 0) > 0 && num(d.height, 0) > 0 ? num(d.width, 1) / num(d.height, 1) : 1;

  // Field → paramName bindings that expose a control as a batch/API input.
  const bindings = (d.params && typeof d.params === 'object' ? (d.params as Record<string, string>) : {});
  const bind = (key: string, name: string | null) => {
    const next = { ...bindings };
    if (name == null) delete next[key];
    else next[key] = name;
    update(id, { params: next });
  };

  // The concrete control for a param (unbound state).
  const controlFor = (p: ParamField) =>
    p.kind === 'range' ? (
      <label className="text-[11px] text-[var(--text-muted)] block">
        <span className="flex justify-between items-center gap-2">
          <span>{p.label}</span>
          <MirroredInput
            type="number"
            className="input nodrag w-14 text-[11px] py-0 px-1 text-right text-[var(--text-secondary)]"
            min={p.min}
            max={p.max}
            step={p.step}
            value={Number(d[p.key] ?? p.min)}
            onCommit={(v) => {
              if (v === '') return;
              const n = Number(v);
              if (Number.isNaN(n)) return;
              update(id, { [p.key]: Math.max(p.min, Math.min(p.max, n)) });
            }}
          />
        </span>
        <input
          type="range"
          className="nodrag w-full accent-[var(--accent)]"
          min={p.min}
          max={p.max}
          step={p.step ?? 1}
          value={Number(d[p.key] ?? p.min)}
          onChange={(e) => update(id, { [p.key]: Number(e.target.value) })}
        />
      </label>
    ) : p.kind === 'color' ? (
      <label className="text-[11px] text-[var(--text-muted)] flex items-center justify-between gap-2">
        <span>{p.label}</span>
        <input
          type="color"
          className="nodrag h-6 w-10 rounded cursor-pointer bg-transparent border border-[var(--border-subtle)]"
          value={String(d[p.key] ?? '#000000')}
          onChange={(e) => update(id, { [p.key]: e.target.value })}
        />
      </label>
    ) : (
      <label className="text-[11px] text-[var(--text-muted)] block">
        {p.label}
        {p.kind === 'select' ? (
          <select
            className="input nodrag w-full text-xs mt-0.5"
            value={String(d[p.key] ?? '')}
            onChange={(e) => update(id, { [p.key]: e.target.value })}
          >
            {p.options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        ) : p.kind === 'checkbox' ? (
          <input
            type="checkbox"
            className="nodrag ml-2 align-middle"
            checked={!!d[p.key]}
            onChange={(e) => update(id, { [p.key]: e.target.checked })}
          />
        ) : (
          <MirroredInput
            type="number"
            className="input nodrag w-full text-xs mt-0.5"
            min={p.min}
            max={p.max}
            step={p.step}
            value={(d[p.key] as number | string | undefined) ?? ''}
            onCommit={(v) => update(id, { [p.key]: v === '' ? null : Number(v) })}
          />
        )}
      </label>
    );

  const renderParam = (p: ParamField) => {
    const bound = bindings[p.key];
    return (
      <div key={p.key} className="flex items-start gap-1 group">
        {bound !== undefined ? (
          <div className="flex-1 min-w-0 flex items-center gap-1 text-[11px] text-[var(--text-muted)]">
            <span className="shrink-0 text-[var(--accent)] font-mono" title="Set per batch row / API input">
              {'{}'}
            </span>
            <span className="shrink-0 truncate max-w-[70px]">{p.label}</span>
            <MirroredInput
              className="input nodrag flex-1 min-w-0 text-[11px] py-0 px-1"
              value={bound}
              onCommit={(v) => bind(p.key, v.trim() || p.key)}
              placeholder={p.key}
            />
            <button
              className="nodrag shrink-0 text-[var(--text-muted)] hover:text-[var(--error)]"
              title="Unbind"
              onClick={() => bind(p.key, null)}
            >
              ✕
            </button>
          </div>
        ) : (
          <>
            <button
              className="nodrag mt-1 shrink-0 font-mono text-[9px] leading-none text-[var(--text-muted)] hover:text-[var(--accent)] opacity-30 group-hover:opacity-100 transition-opacity"
              title="Expose as a batch / API param"
              onClick={() => bind(p.key, p.key)}
            >
              {'{}'}
            </button>
            <div className="flex-1 min-w-0">{controlFor(p)}</div>
          </>
        )}
      </div>
    );
  };

  const resultSrc = livePreview ?? out?.image ?? out?.mask;

  return (
    <NodeShell id={id} type={nodeType}>
      {pos &&
        (pos.mode === 'point' ? (
          <PlacementPad
            mode="point"
            image={padImage}
            aspect={padAspect}
            x={num(d.x, 0.5)}
            y={num(d.y, 0.5)}
            onChange={(x, y) => update(id, { x: round2(x), y: round2(y) })}
          />
        ) : (
          <PlacementPad
            mode="rect"
            image={padImage}
            aspect={padAspect}
            rect={{ x1: num(d.x1, 0.2), y1: num(d.y1, 0.2), x2: num(d.x2, 0.8), y2: num(d.y2, 0.8) }}
            onChange={(r) =>
              update(id, { x1: round2(r.x1), y1: round2(r.y1), x2: round2(r.x2), y2: round2(r.y2) })
            }
          />
        ))}

      {params.length > COLLAPSE_THRESHOLD ? (
        <>
          <button
            className="nodrag w-full text-left text-[11px] text-[var(--text-muted)] hover:text-[var(--text-secondary)] flex items-center gap-1"
            onClick={() => setOpen((o) => !o)}
          >
            <span className="inline-block w-3 text-center">{open ? '▾' : '▸'}</span>
            {open ? 'Hide settings' : 'Settings'} ({params.length})
          </button>
          {open && <div className="space-y-2">{params.map(renderParam)}</div>}
        </>
      ) : (
        params.map(renderParam)
      )}

      {/* Positional nodes already show the result inside the pad backdrop.
          Live preview (if any) takes precedence over the last Run's output. */}
      {!pos && resultSrc && (
        // eslint-disable-next-line @next/next/no-img-element
        <img src={resultSrc} alt="result" className="rounded-lg w-full mt-1" />
      )}
      {out?._info && (
        <div className="text-[11px] text-[var(--text-secondary)] mt-1 line-clamp-3">{out._info}</div>
      )}
      {out?._ms != null && (
        <div className="text-[10px] text-[var(--text-muted)]">
          {out._faces != null ? `${out._faces} faces · ` : ''}
          {Math.round(out._ms)}ms
        </div>
      )}
    </NodeShell>
  );
}
